import { useState, useEffect, useRef, useCallback, useMemo } from 'preact/hooks';
import { api } from '../api/client';

import { T } from '../styles/tokens';
import { Button, Input } from '../components/ui';
// ── Types ────────────────────────────────────────────────────────────────

interface FlowNode {
  id: string;
  label: string;
  type: 'page' | 'api' | 'handler' | 'service' | 'table' | 'external';
  file?: string;
  meta?: Record<string, any>;
}

interface FlowEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
}

interface ModuleData {
  id: string;
  name: string;
  icon: string;
  simpleDesc: string;
  techDesc: string;
  nodes: { pages: FlowNode[]; apis: FlowNode[]; handlers: FlowNode[]; services: FlowNode[]; tables: FlowNode[] };
  stats: { pages: number; apis: number; handlers: number; services: number; tables: number };
  totalNodes: number;
}

interface ModuleEdge {
  id: string;
  source: string;
  target: string;
  weight: number;
}

interface FlowResponse {
  modules: ModuleData[];
  edges: ModuleEdge[];
  internalEdges: Record<string, FlowEdge[]>;
  stats: { totalModules: number; totalNodes: number; totalEdges: number };
  hasGithub: boolean;
  debug?: string[];
}

// ── Constants ────────────────────────────────────────────────────────────

const MODULE_COLORS: Record<string, string> = {
  auth: T.primary, cloud: '#06b6d4', admin: T.textMuted, vault: T.success,
  crm: T.purple, router: T.warning, marketplace: T.danger, intelligence: T.danger,
  skills: T.success, runners: T.warning, integrations: T.accent, devmode: '#84cc16',
  notifications: T.warning, other: '#78716c',
};

const NODE_COLORS: Record<string, string> = {
  page: T.primary, api: T.success, handler: T.warning, service: T.purple, table: '#78716c',
};

const NODE_LABELS: Record<string, string> = {
  page: 'Page', api: 'API', handler: 'Handler', service: 'Service', table: 'Table',
};

// ── Component ────────────────────────────────────────────────────────────

export function AdminFlow() {
  const [data, setData] = useState<FlowResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedModule, setSelectedModule] = useState<string | null>(null);
  const [hoveredModule, setHoveredModule] = useState<string | null>(null);
  const [detailMode, setDetailMode] = useState(false);
  const [tab, setTab] = useState<'simple' | 'tech'>('simple');
  const [search, setSearch] = useState('');
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const svgRef = useRef<SVGSVGElement>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res: any = await api.get('/admin/flow/analyze');
      setData(res as FlowResponse);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, []);

  // Active module for right panel
  const activeModuleId = selectedModule || hoveredModule;
  const activeModule = useMemo(() => {
    if (!data || !activeModuleId) return null;
    return data.modules.find(m => m.id === activeModuleId) || null;
  }, [data, activeModuleId]);

  // Connected module IDs for highlighting
  const connectedModuleIds = useMemo(() => {
    if (!data || !activeModuleId) return new Set<string>();
    const ids = new Set<string>([activeModuleId]);
    for (const e of data.edges) {
      if (e.source === activeModuleId) ids.add(e.target);
      if (e.target === activeModuleId) ids.add(e.source);
    }
    return ids;
  }, [data, activeModuleId]);

  // Search filter
  const searchLower = search.toLowerCase();
  const filteredModules = useMemo(() => {
    if (!data) return [];
    if (!searchLower) return data.modules.filter(m => m.totalNodes > 0);
    return data.modules.filter(m => m.totalNodes > 0 && (
      m.name.toLowerCase().includes(searchLower) || m.id.includes(searchLower)
    ));
  }, [data, searchLower]);

  // ── Overview layout: grid of modules ──
  const modulePositions = useMemo(() => {
    const mods = filteredModules;
    const cols = Math.ceil(Math.sqrt(mods.length * 1.5));
    const pos: Record<string, { x: number; y: number }> = {};
    const W = 200, H = 120, GAP_X = 60, GAP_Y = 50;
    mods.forEach((m, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      pos[m.id] = { x: col * (W + GAP_X) + 40, y: row * (H + GAP_Y) + 40 };
    });
    return pos;
  }, [filteredModules]);

  // ── Detail layout: internal nodes in layers ──
  const detailModule = useMemo(() => {
    if (!detailMode || !selectedModule || !data) return null;
    return data.modules.find(m => m.id === selectedModule) || null;
  }, [detailMode, selectedModule, data]);

  const detailNodePositions = useMemo(() => {
    if (!detailModule) return {};
    const pos: Record<string, { x: number; y: number }> = {};
    const layers: FlowNode[][] = [
      detailModule.nodes.pages,
      detailModule.nodes.apis,
      detailModule.nodes.handlers,
      detailModule.nodes.services,
      detailModule.nodes.tables,
    ];
    const W = 160, H = 36, LAYER_GAP = 240, NODE_GAP = 48;
    const maxH = Math.max(...layers.map(l => l.length), 1);
    layers.forEach((layer, li) => {
      const offsetY = ((maxH - layer.length) * NODE_GAP) / 2;
      layer.forEach((n, ni) => {
        pos[n.id] = { x: li * LAYER_GAP + 40, y: ni * NODE_GAP + offsetY + 40 };
      });
    });
    return pos;
  }, [detailModule]);

  const detailEdges = useMemo(() => {
    if (!detailMode || !selectedModule || !data) return [];
    return data.internalEdges?.[selectedModule] || [];
  }, [detailMode, selectedModule, data]);

  // ── Pan/Zoom handlers ──
  const onWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(z => Math.max(0.1, Math.min(5, z * delta)));
  }, []);

  const onPointerDown = useCallback((e: PointerEvent) => {
    if ((e.target as Element).closest('.flow-node')) return;
    setDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  }, [pan]);

  const onPointerMove = useCallback((e: PointerEvent) => {
    if (!dragging) return;
    setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  }, [dragging, dragStart]);

  const onPointerUp = useCallback(() => setDragging(false), []);

  const fitAll = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const enterDetail = useCallback((modId: string) => {
    setSelectedModule(modId);
    setDetailMode(true);
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const exitDetail = useCallback(() => {
    setDetailMode(false);
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  // ── Edge path helper ──
  const bezierPath = useCallback((sx: number, sy: number, tx: number, ty: number) => {
    const dx = Math.abs(tx - sx) * 0.4;
    return `M${sx},${sy} C${sx + dx},${sy} ${tx - dx},${ty} ${tx},${ty}`;
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ background: T.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.textSecondary }}>
        <div style={{ textAlign: 'center' }}>
 <div style={{ fontSize: 40, marginBottom: 16, animation: 'pulse 1.5s infinite' }}></div>
          <div style={{ fontSize: 14 }}>Analyzing system architecture...</div>
        </div>
        <style>{`@keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.4 } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: T.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.danger }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
 <div style={{ fontSize: 40, marginBottom: 16 }}></div>
          <div style={{ fontSize: 14, marginBottom: 12 }}>{error}</div>
          <Button variant="secondary" onClick={fetchData}>Retry</Button>
        </div>
      </div>
    );
  }

  if (!data || (!data.hasGithub && data.stats.totalNodes === 0)) {
    return (
      <div style={{ background: T.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.textSecondary }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
 <div style={{ fontSize: 40, marginBottom: 16 }}></div>
          <div style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 8 }}>GitHub Not Configured</div>
          <div style={{ fontSize: 13 }}>Configure GitHub in Admin → GitHub to enable architecture analysis.</div>
        </div>
      </div>
    );
  }

  const MW = 200, MH = 120; // module block size
  const NW = 160, NH = 36; // detail node size

  return (
    <div style={{ background: T.bg, height: '100vh', display: 'flex', flexDirection: 'column', color: T.text, overflow: 'hidden' }}>
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid #1a1a1a', background: '#0f0f0f', flexShrink: 0 }}>
 <span style={{ fontSize: 20 }}></span>
        <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>System Flow Map</span>
        {detailMode && detailModule && (
          <span style={{ fontSize: 13, color: T.textSecondary }}>
            {'>'} <span style={{ color: MODULE_COLORS[detailModule.id] || T.textSecondary }}>{detailModule.name}</span>
          </span>
        )}
        <div style={{ flex: 1 }} />
        <Input
          placeholder="Search..." value={search}
          onChange={(v) => setSearch(v)}
          style={{ padding: '6px 12px', background: T.bgSecondary, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 13, width: 180, outline: 'none' }}
        />
 <Button variant="ghost" size="xs" onClick={() => setZoom(z =>Math.min(5, z * 1.2))}></Button>
 <Button variant="ghost" size="xs" onClick={() => setZoom(z =>Math.max(0.1, z / 1.2))}></Button>
 <Button variant="ghost" size="xs" onClick={fitAll}></Button>
        {detailMode && <Button variant="secondary" size="xs" onClick={exitDetail}>← Back</Button>}
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* ── Left Sidebar: Module List ── */}
        <div style={{ width: 200, borderRight: '1px solid #1a1a1a', background: T.bg, overflow: 'auto', flexShrink: 0, padding: '12px 0' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: T.textMuted, padding: '0 12px', marginBottom: 8, letterSpacing: 1 }}>Módulos</div>
          {data.modules.filter(m => m.totalNodes > 0).map(m => {
            const isActive = selectedModule === m.id;
            const color = MODULE_COLORS[m.id] || T.textSecondary;
            return (
              <div
                key={m.id}
                onClick={() => { setSelectedModule(m.id); if (!detailMode) setHoveredModule(null); }}
                onDblClick={() => enterDetail(m.id)}
                onMouseEnter={() => !detailMode && setHoveredModule(m.id)}
                onMouseLeave={() => !detailMode && setHoveredModule(null)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', cursor: 'pointer',
                  background: isActive ? color + '1a' : 'transparent',
                  borderLeft: isActive ? `3px solid ${color}` : '3px solid transparent',
                  transition: 'all 0.15s',
                }}
              >
                <span style={{ fontSize: 9, fontFamily: 'monospace', color: color, fontWeight: 700, minWidth: 36 }}>{m.icon}</span>
                <span style={{ fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: isActive ? '#fff' : '#bbb' }}>{m.name}</span>
                <span style={{ fontSize: 11, color: T.textMuted, fontFamily: 'monospace' }}>{m.totalNodes}</span>
              </div>
            );
          })}
          <div style={{ borderTop: '1px solid #1a1a1a', margin: '12px 12px 8px', padding: '8px 0 0' }}>
            <div style={{ fontSize: 11, color: T.textMuted }}>
              Total: <b style={{ color: T.textSecondary }}>{data.stats.totalNodes}</b> nós
            </div>
            <div style={{ fontSize: 11, color: T.textMuted }}>
              Módulos: <b style={{ color: T.textSecondary }}>{data.stats.totalModules}</b>
            </div>
            <div style={{ fontSize: 11, color: T.textMuted }}>
              Conexões: <b style={{ color: T.textSecondary }}>{data.stats.totalEdges}</b>
            </div>
          </div>
        </div>

        {/* ── Center: SVG Diagram ── */}
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          <svg
            ref={svgRef} width="100%" height="100%"
            style={{ cursor: dragging ? 'grabbing' : 'grab', background: T.bg }}
            onWheel={onWheel as any}
            onPointerDown={onPointerDown as any}
            onPointerMove={onPointerMove as any}
            onPointerUp={onPointerUp as any}
            onPointerLeave={onPointerUp as any}
          >
            <defs>
              <marker id="arrow" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"><polygon points="0 0, 8 3, 0 6" fill={T.textPrimary} /></marker>
              <marker id="arrow-hi" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"><polygon points="0 0, 8 3, 0 6" fill={T.primary} /></marker>
              <filter id="glow"><feGaussianBlur stdDeviation="4" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
            </defs>

            <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
              {!detailMode ? (
                <>
                  {/* ── OVERVIEW: Module edges ── */}
                  {data.edges.map(e => {
                    const sp = modulePositions[e.source];
                    const tp = modulePositions[e.target];
                    if (!sp || !tp) return null;
                    const active = activeModuleId && (e.source === activeModuleId || e.target === activeModuleId);
                    const dim = activeModuleId && !active;
                    return (
                      <path
                        key={e.id}
                        d={bezierPath(sp.x + MW, sp.y + MH / 2, tp.x, tp.y + MH / 2)}
                        fill="none" stroke={active ? T.primary : T.textPrimary} stroke-width={active ? 2 : 1}
                        stroke-opacity={dim ? 0.08 : active ? 0.8 : 0.3}
                        marker-end={active ? 'url(#arrow-hi)' : 'url(#arrow)'}
                        stroke-dasharray={active ? '6 3' : 'none'}
                      >
                        {active && <animate attributeName="stroke-dashoffset" from="18" to="0" dur="1s" repeatCount="indefinite" />}
                      </path>
                    );
                  })}

                  {/* ── OVERVIEW: Module blocks ── */}
                  {filteredModules.map(m => {
                    const pos = modulePositions[m.id];
                    if (!pos) return null;
                    const color = MODULE_COLORS[m.id] || T.textSecondary;
                    const isActive = selectedModule === m.id || hoveredModule === m.id;
                    const dim = activeModuleId && !connectedModuleIds.has(m.id);
                    return (
                      <g
                        key={m.id} class="flow-node"
                        transform={`translate(${pos.x},${pos.y})`}
                        style={{ cursor: 'pointer', opacity: dim ? 0.2 : 1, transition: 'opacity 0.2s' }}
                        onClick={() => setSelectedModule(m.id === selectedModule ? null : m.id)}
                        onDblClick={() => enterDetail(m.id)}
                        onMouseEnter={() => setHoveredModule(m.id)}
                        onMouseLeave={() => setHoveredModule(null)}
                      >
                        <rect
                          width={MW} height={MH} rx={12} ry={12}
                          fill={color + '1a'} stroke={isActive ? color : color + '66'}
                          stroke-width={isActive ? 2 : 1}
                          filter={isActive ? 'url(#glow)' : undefined}
                        />
                        <text x={MW / 2} y={36} text-anchor="middle" fill="#fff" font-size="11" font-weight="700" font-family="monospace" letter-spacing="1" opacity="0.5">{m.icon}</text>
                        <text x={MW / 2} y={60} text-anchor="middle" fill={color} font-size="13" font-weight="600" font-family="system-ui, sans-serif">{m.name}</text>
                        <text x={MW / 2} y={80} text-anchor="middle" fill="#666" font-size="11" font-family="monospace">{m.totalNodes} nós</text>
                        {/* Mini stat bar */}
                        {(() => {
                          const total = m.totalNodes || 1;
                          const barW = MW - 40;
                          const barY = 95;
                          let cx = 20;
                          const segments = [
                            { count: m.stats.pages, color: NODE_COLORS.page },
                            { count: m.stats.apis, color: NODE_COLORS.api },
                            { count: m.stats.handlers, color: NODE_COLORS.handler },
                            { count: m.stats.services, color: NODE_COLORS.service },
                            { count: m.stats.tables, color: NODE_COLORS.table },
                          ];
                          return segments.map((s, i) => {
                            const w = (s.count / total) * barW;
                            if (w < 1) { cx += w; return null; }
                            const el = <rect key={i} x={cx} y={barY} width={w} height={4} rx={2} fill={s.color} opacity={0.6} />;
                            cx += w;
                            return el;
                          });
                        })()}
                      </g>
                    );
                  })}
                </>
              ) : detailModule ? (
                <>
                  {/* ── DETAIL: Internal edges ── */}
                  {detailEdges.map(e => {
                    const sp = detailNodePositions[e.source];
                    const tp = detailNodePositions[e.target];
                    if (!sp || !tp) return null;
                    return (
                      <path key={e.id}
                        d={bezierPath(sp.x + NW, sp.y + NH / 2, tp.x, tp.y + NH / 2)}
                        fill="none" stroke={T.textPrimary} stroke-width={1} stroke-opacity={0.5}
                        marker-end="url(#arrow)"
                      />
                    );
                  })}

                  {/* ── DETAIL: Layer labels ── */}
                  {['Pages', 'APIs', 'Handlers', 'Services', 'Tables'].map((label, i) => (
                    <text key={label} x={i * 240 + 40 + NW / 2} y={20} text-anchor="middle" fill="#444" font-size="11" font-weight="600" font-family="system-ui">{label}</text>
                  ))}

                  {/* ── DETAIL: Internal nodes ── */}
                  {Object.entries(detailNodePositions).map(([nodeId, pos]) => {
                    const allNodes = [
                      ...detailModule.nodes.pages,
                      ...detailModule.nodes.apis,
                      ...detailModule.nodes.handlers,
                      ...detailModule.nodes.services,
                      ...detailModule.nodes.tables,
                    ];
                    const node = allNodes.find(n => n.id === nodeId);
                    if (!node) return null;
                    const color = NODE_COLORS[node.type] || T.textSecondary;
                    return (
                      <g key={nodeId} class="flow-node" transform={`translate(${pos.x},${pos.y})`} style={{ cursor: 'pointer' }}>
                        <rect width={NW} height={NH} rx={6} ry={6} fill={color + '1a'} stroke={color + '66'} stroke-width={1} />
                        <text x={NW / 2} y={NH / 2 + 1} text-anchor="middle" dominant-baseline="middle" fill={color} font-size="10" font-family="'JetBrains Mono', monospace">
                          {node.label.length > 20 ? node.label.slice(0, 19) + '…' : node.label}
                        </text>
                      </g>
                    );
                  })}
                </>
              ) : null}
            </g>
          </svg>

          {/* Hint */}
          {!detailMode && (
            <div style={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', background: '#111c', padding: '6px 16px', borderRadius: 8, fontSize: 11, color: T.textMuted }}>
              Clique para selecionar · Duplo-clique para detalhe · Scroll para zoom
            </div>
          )}
        </div>

        {/* ── Right Panel: Explanation ── */}
        <div style={{ width: 300, borderLeft: '1px solid #1a1a1a', background: T.bg, overflow: 'auto', flexShrink: 0, padding: '16px' }}>
          {activeModule ? (
            <>
              {/* Module header */}
              <div style={{ fontSize: 11, fontFamily: 'monospace', color: MODULE_COLORS[activeModule.id] || T.textSecondary, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>{activeModule.icon}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: MODULE_COLORS[activeModule.id] || '#fff', marginBottom: 12 }}>{activeModule.name}</div>

              {/* Tabs */}
              <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderRadius: 8, overflow: 'hidden', border: '1px solid #222' }}>
                <Button variant={tab === 'simple' ? 'secondary' : 'ghost'} size="sm" onClick={() => setTab('simple')} style={{ flex: 1, borderRadius: 0 }}>Simples</Button>
                <Button variant={tab === 'tech' ? 'secondary' : 'ghost'} size="sm" onClick={() => setTab('tech')} style={{ flex: 1, borderRadius: 0 }}>Técnico</Button>
              </div>

              {/* Description */}
              <div style={{ fontSize: 13, lineHeight: 1.7, color: '#bbb', marginBottom: 20, fontFamily: tab === 'tech' ? "'JetBrains Mono', monospace" : 'system-ui, sans-serif', whiteSpace: 'pre-wrap' }}>
                {tab === 'simple' ? activeModule.simpleDesc : activeModule.techDesc}
              </div>

              {/* Stats */}
              <div style={{ borderTop: '1px solid #1a1a1a', paddingTop: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: T.textMuted, marginBottom: 8, letterSpacing: 1 }}>Composição</div>
                {[
                  { label: 'Páginas', count: activeModule.stats.pages, color: NODE_COLORS.page, icon: '' },
                  { label: 'APIs', count: activeModule.stats.apis, color: NODE_COLORS.api, icon: '' },
                  { label: 'Handlers', count: activeModule.stats.handlers, color: NODE_COLORS.handler, icon: '' },
                  { label: 'Services', count: activeModule.stats.services, color: NODE_COLORS.service, icon: '' },
                  { label: 'Tabelas', count: activeModule.stats.tables, color: NODE_COLORS.table, icon: '' },
                ].filter(s => s.count > 0).map(s => (
                  <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 12 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, display: 'inline-block' }} />
                    <span style={{ flex: 1, color: T.textSecondary }}>{s.label}</span>
                    <span style={{ color: s.color, fontWeight: 600, fontFamily: 'monospace' }}>{s.count}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0 0', fontSize: 12, borderTop: '1px solid #1a1a1a', marginTop: 6 }}>
 <span></span>
                  <span style={{ flex: 1, color: T.textSecondary }}>Total</span>
                  <span style={{ color: T.text, fontWeight: 700, fontFamily: 'monospace' }}>{activeModule.totalNodes}</span>
                </div>
              </div>

              {/* Tech tab: list nodes */}
              {tab === 'tech' && (
                <div style={{ borderTop: '1px solid #1a1a1a', paddingTop: 12 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: T.textMuted, marginBottom: 8, letterSpacing: 1 }}>Nós</div>
                  {(['pages', 'apis', 'handlers', 'services', 'tables'] as const).map(bucket => {
                    const nodes = activeModule.nodes[bucket];
                    if (!nodes.length) return null;
                    return (
                      <div key={bucket} style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 4, textTransform: 'uppercase' }}>{bucket}</div>
                        {nodes.map(n => (
                          <div key={n.id} style={{ fontSize: 11, color: T.textSecondary, fontFamily: 'monospace', padding: '1px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {n.label}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            /* System overview when nothing selected */
            <>
 <div style={{ fontSize: 28, marginBottom: 4 }}></div>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 12 }}>System Overview</div>
              <div style={{ fontSize: 13, lineHeight: 1.7, color: T.textSecondary, marginBottom: 20 }}>
                Selecione um módulo no diagrama ou na lista à esquerda para ver sua descrição detalhada.
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.7, color: T.textSecondary }}>
                O sistema possui <b style={{ color: T.text }}>{data.stats.totalModules}</b> módulos,{' '}
                <b style={{ color: T.text }}>{data.stats.totalNodes}</b> componentes e{' '}
                <b style={{ color: T.text }}>{data.stats.totalEdges}</b> conexões.
              </div>
              <div style={{ marginTop: 20, borderTop: '1px solid #1a1a1a', paddingTop: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: T.textMuted, marginBottom: 8, letterSpacing: 1 }}>Legenda</div>
                {Object.entries(NODE_COLORS).map(([type, color]) => (
                  <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 12 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: color, flexShrink: 0 }} />
                    <span style={{ color: T.textSecondary }}>{NODE_LABELS[type] || type}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`@keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.4 } }`}</style>
    </div>
  );
}

const btnStyle: any = {
  padding: '6px 10px', background: T.bgSecondary, border: `1px solid ${T.border}`, borderRadius: 8,
  color: T.text, cursor: 'pointer', fontSize: 14, lineHeight: 1,
};
